import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { DatePicker } from '../components/ui/date-picker';

interface ServiceReport {
  id: string;
  appointmentCode?: string;
  reportNumber: string;
  serviceDate: string;
  serviceType: string;
  workDescription: string;
  status: string;
  client: {
    firstName: string;
    lastName: string;
    phone: string;
  };
  vehicle: {
    licensePlate: string;
    brand: string;
    model: string;
    year?: number;
  };
  technician: {
    firstName: string;
    lastName: string;
  };
}

const ActiveServicesArchive: React.FC = () => {
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [archiveReports, setArchiveReports] = useState<ServiceReport[]>([]);
  const [archiveQuery, setArchiveQuery] = useState('');
  const [archiveStatus, setArchiveStatus] = useState('all');
  const [archiveFrom, setArchiveFrom] = useState('');
  const [archiveTo, setArchiveTo] = useState('');

  React.useEffect(() => {
    const loadArchive = async () => {
      try {
        setLoading(true);
        const [completedResponse, cancelledResponse] = await Promise.all([
          axios.get('/api/service-reports?status=completed'),
          axios.get('/api/service-reports?status=cancelled')
        ]);
        const completed = completedResponse.data.reports || [];
        const cancelled = cancelledResponse.data.reports || [];
        const merged = [...completed, ...cancelled].sort((a, b) => {
          const aDate = new Date(a.serviceDate).getTime();
          const bDate = new Date(b.serviceDate).getTime();
          return bDate - aDate;
        });
        setArchiveReports(merged);
      } catch (error) {
        toast.error('Error al cargar el archivo');
      } finally {
        setLoading(false);
      }
    };
    loadArchive();
  }, []);

  const filteredArchiveReports = useMemo(() => {
    const query = archiveQuery.trim().toLowerCase();
    const fromDate = archiveFrom ? new Date(`${archiveFrom}T00:00:00`) : null;
    const toDate = archiveTo ? new Date(`${archiveTo}T23:59:59`) : null;

    return archiveReports.filter((report) => {
      if (archiveStatus !== 'all' && report.status !== archiveStatus) {
        return false;
      }

      if (fromDate || toDate) {
        const serviceDate = report.serviceDate ? new Date(report.serviceDate) : null;
        if (!serviceDate) return false;
        if (fromDate && serviceDate < fromDate) return false;
        if (toDate && serviceDate > toDate) return false;
      }

      if (!query) return true;

      const clientName = `${report.client?.firstName || ''} ${report.client?.lastName || ''}`.trim().toLowerCase();
      const vehicleBrand = (report.vehicle?.brand || '').toLowerCase();
      const vehicleModel = (report.vehicle?.model || '').toLowerCase();
      const plate = (report.vehicle?.licensePlate || '').toLowerCase();
      const reportNumber = (report.reportNumber || '').toLowerCase();
      const appointmentCode = (report.appointmentCode || '').toLowerCase();
      const serviceType = (report.serviceType || '').toLowerCase();

      return [
        clientName,
        vehicleBrand,
        vehicleModel,
        plate,
        reportNumber,
        appointmentCode,
        serviceType
      ].some((value) => value.includes(query));
    });
  }, [archiveReports, archiveQuery, archiveStatus, archiveFrom, archiveTo]);

  const getStatusColor = (status: string) => {
    if (status === 'completed') return 'bg-emerald-100 text-emerald-700';
    if (status === 'cancelled') return 'bg-red-100 text-red-700';
    return 'bg-slate-100 text-slate-700';
  };

  const getStatusText = (status: string) => {
    if (status === 'completed') return 'Completado';
    if (status === 'cancelled') return 'Cancelado';
    return status;
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className={`text-lg font-medium transition-colors duration-200 ${
            isDarkMode ? 'text-gray-100' : 'text-gray-900'
          }`}>
            Archivo de servicios
          </h3>
          <p className="text-sm text-muted-foreground">
            Completados y cancelados
          </p>
        </div>
        <Button variant="outline" onClick={() => navigate(-1)}>
          Volver
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Filtros</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Buscar</label>
              <input
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                placeholder="Cliente, patente, OT, servicio..."
                value={archiveQuery}
                onChange={(event) => setArchiveQuery(event.target.value)}
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Estado</label>
              <select
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                value={archiveStatus}
                onChange={(event) => setArchiveStatus(event.target.value)}
              >
                <option value="all">Todos</option>
                <option value="completed">Completados</option>
                <option value="cancelled">Cancelados</option>
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Desde</label>
              <DatePicker
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                value={archiveFrom}
                onChange={setArchiveFrom}
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Hasta</label>
              <DatePicker
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                value={archiveTo}
                onChange={setArchiveTo}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Servicios</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-sm text-muted-foreground">Cargando archivo...</p>
          ) : filteredArchiveReports.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No hay servicios finalizados para mostrar.
            </p>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {filteredArchiveReports.map((report) => {
                const clientName = `${report.client?.firstName || ''} ${report.client?.lastName || ''}`.trim();
                const vehicleLabel = [report.vehicle?.brand, report.vehicle?.model].filter(Boolean).join(' ');
                const plate = report.vehicle?.licensePlate || '';
                return (
                  <div key={report.id} className="rounded-lg border p-4 space-y-2">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-semibold">{report.appointmentCode || report.reportNumber}</p>
                        <p className="text-xs text-muted-foreground">
                          {report.serviceType}
                        </p>
                      </div>
                      <Badge className={getStatusColor(report.status)}>
                        {getStatusText(report.status)}
                      </Badge>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {clientName || 'Cliente sin asignar'}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {[vehicleLabel, plate].filter(Boolean).join(' - ') || '-'}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {new Date(report.serviceDate).toLocaleDateString('es-AR')}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ActiveServicesArchive;
